import { Injectable } from '@jack-henry/frontend-utils/di';
import { createUniqueId } from '@jack-henry/frontend-utils/functions';
import { AlarmClock } from '@treasury/alarm-clock';
import {
    ArpClient,
    ArpExceptionDetailDataModelDto,
    CheckExceptionClient,
    CheckExceptionCorrectionClient,
    CompanyAccountModelDto,
    CompanyAccountsClient,
    InstitutionCheckExceptionReturnReasonClient,
    PositivePayCompanyConfigurationClient,
    ProcessingCutoffDto,
    SaveCheckExceptionAttachmentRequestModelDto,
} from '@treasury/api/channel';
import { LoggingService } from '@treasury/core/logging';
import {
    CHECK_EXCEPTION_CUTOFF,
    CHECK_EXCEPTION_CUTOFF_MINUS_30,
    CHECK_EXCEPTION_START,
} from '../channel/types/arp/constants';
import { downloadBlob, toBase64 } from '../utilities/file-handling';
import { CheckException } from './check-exception.entity';
import { CompanyOptionModel } from './company-option-model.entity';
import { ExceptionCorrection } from './exception-correction.entity';
import { InstitutionCheckExceptionReturnReason } from './institution-check-exception-return-reason.entity';
import { mapSearchParams } from './mappings/check-exception-search.mappers';

@Injectable()
export class CheckExceptionService {
    constructor(
        private arpClient: ArpClient,
        private checkCorrectionClient: CheckExceptionCorrectionClient,
        private checkExceptionClient: CheckExceptionClient,
        private returnReasonClient: InstitutionCheckExceptionReturnReasonClient,
        private companyAccountsClient: CompanyAccountsClient,
        private positivePayClient: PositivePayCompanyConfigurationClient,
        private logger: LoggingService
    ) {}

    public testId = createUniqueId();

    private initPromise?: Promise<
        [ProcessingCutoffDto | undefined, InstitutionCheckExceptionReturnReason[], boolean]
    >;

    private _cutoffTimePromise?: Promise<ProcessingCutoffDto | undefined> = this.getCutoffTime();

    private _cutoffTime?: ProcessingCutoffDto;

    private _returnReasonsPromise = this.getReturnReasons();

    private _returnReasons?: InstitutionCheckExceptionReturnReason[];

    public get cutoffTime() {
        return this._cutoffTime;
    }

    public get returnReasons() {
        if (!this._returnReasons) {
            throw new Error('Check exception return reasons initialization has not resolved');
        }
        return this._returnReasons;
    }

    private get fiClock() {
        return AlarmClock.getInstance();
    }

    public get isAfterCutoff(): boolean {
        if (
            this.fiClock.isAfter(CHECK_EXCEPTION_CUTOFF_MINUS_30) &&
            this.fiClock.isBefore(CHECK_EXCEPTION_CUTOFF)
        ) {
            return false;
        }
        return this.fiClock.isAfter(CHECK_EXCEPTION_CUTOFF);
    }

    public get isBeforeCutoff(): boolean {
        return this.fiClock.isBefore(CHECK_EXCEPTION_START);
    }

    /**
     * Before this service can be used, ready must resolve.
     * It can be called at any time to ensure that the service is initialized.
     *
     * @returns Promise<[]ProcessingCutoffDto | undefined, InstitutionCheckExceptionReturnReason[], boolean]>
     */
    public async ready(): Promise<
        [ProcessingCutoffDto | undefined, InstitutionCheckExceptionReturnReason[], boolean]
    > {
        if (!this.initPromise) {
            this.initPromise = Promise.all([
                this._cutoffTimePromise,
                this._returnReasonsPromise,
                this.fiClock.checkReadiness(),
            ]);
            [this._cutoffTime, this._returnReasons] = await this.initPromise;
        }
        return this.initPromise;
    }

    public async getCheckExceptions(): Promise<CheckException[]> {
        const arpExceptionAccounts = (
            await this.arpClient.arpGetArpAccounts('WorkArpExceptions', {})
        ).data.map((account: CompanyAccountModelDto) => account.accountUniqueId);
        const parameters = { account: arpExceptionAccounts };
        const body = mapSearchParams(parameters);
        const searchCheckExceptionsResponse = (
            await this.checkExceptionClient.checkExceptionSearchCheckExceptions(body)
        ).data;
        if (!searchCheckExceptionsResponse.items) {
            return [];
        }
        return searchCheckExceptionsResponse.items.map(exception => new CheckException(exception));
    }

    public async getCutoffTime(): Promise<ProcessingCutoffDto | undefined> {
        return (
            await this.companyAccountsClient.companyAccountsGetCutoffTimes()
        ).data.processingCutoffs?.find(cutoff => cutoff.productType === 'Check Exceptions');
    }

    public async getReturnReasons(): Promise<InstitutionCheckExceptionReturnReason[]> {
        return (
            await this.returnReasonClient.institutionCheckExceptionReturnReasonGetCheckExceptionReturnReasons()
        ).data.map(reason => new InstitutionCheckExceptionReturnReason(reason));
    }

    public async getPositivePayCompanyConfiguration() {
        const { data } =
            await this.positivePayClient.positivePayCompanyConfigurationGetAllOptions();
        return data.map(option => new CompanyOptionModel(option));
    }

    public async getAttachmentName(checkExceptionId: string): Promise<string | void> {
        const res = await this.checkExceptionClient.checkExceptionGetAttachment(checkExceptionId);
        return res?.headers?.get('x-filename') || undefined;
    }

    public async downloadAttachment(checkExceptionId: string) {
        const res = await this.checkExceptionClient.checkExceptionGetAttachment(checkExceptionId);

        const filename = res.headers.get('x-filename');
        const contentType = res.headers.get('content-type');
        const buffer = await res.arrayBuffer();
        const blob = new Blob([buffer], { type: contentType ?? '' });
        return downloadBlob(blob, filename);
    }

    public async submitCheckExceptions(checkExceptions: CheckException[]): Promise<boolean> {
        return (
            await this.arpClient.arpSaveArpExceptions(
                checkExceptions.map(
                    exception =>
                        exception.getDtoForSubmit() as unknown as ArpExceptionDetailDataModelDto
                ),
                {}
            )
        ).data;
    }

    public async saveAttachmentAndComment(
        checkException: CheckException,
        comment: string,
        attachment?: File
    ) {
        const body = {
            comment,
        } as SaveCheckExceptionAttachmentRequestModelDto;
        if (attachment) {
            try {
                body.attachment = await toBase64(attachment);
            } catch (e) {
                this.logger.logError(
                    'Failed to save attachment and comment',
                    e instanceof Error ? e : undefined
                );
            }
            body.fileName = attachment?.name;
        }
        return (
            await this.checkExceptionClient.checkExceptionSaveAttachment(checkException.guid, body)
        ).data;
    }

    public async submitCheckExceptionCorrection(correction: ExceptionCorrection) {
        return (
            await this.checkCorrectionClient.checkExceptionCorrectionRequestExceptionCorrection(
                correction.toDto()
            )
        ).data;
    }
}
